"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
	ChevronLeft,
	ExternalLink,
	FolderKanban,
	MessageSquare,
	Minus,
	Plus,
	SendHorizontal,
} from "lucide-react";
import { useSession } from "next-auth/react";

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import LoadingSpinner from "@/components/loadingComponents/loadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import {
	useGetChatsMessagesByIdQuery,
	useGetAllChatsQuery,
	useGetClientSupportGroupChatQuery,
	useMarkChatAsReadMutation,
} from "@/features/chat/chatApiInjection";
import { CHAT_CONSTANTS } from "@/app/profile/inbox/(chats)/constants";
import { ChatDummyContextProvider } from "@/app/admin/chat/context";
import Message from "@/app/profile/inbox/(chats)/[chatId]/components/Message";
import TextBar from "@/app/profile/inbox/(chats)/[chatId]/components/TextBar";
import DateDivider from "@/app/profile/inbox/(chats)/[chatId]/components/DateDivider";
import EmojiPicker from "@/app/profile/inbox/(chats)/[chatId]/components/EmojiPicker";
import TypingIndicator from "@/app/profile/inbox/(chats)/[chatId]/components/TypingIndicator";
import StatusMarker, {
	getUserFacingClientSupportStatus,
} from "@/app/profile/inbox/(chats)/components/ChatBar/StatusMarker";
import SupportTicketsPanel from "@/app/profile/inbox/(chats)/components/SupportTickets/SupportTicketsPanel";
import {
	CHAT_TYPING_EVENT,
	useMessageSubscription,
} from "@/app/profile/inbox/(chats)/hooks/useMessageSubscription";
import ChatIcon from "@/components/icons/ChatIcon";
import SignupEntryCard from "@/components/auth/SignupEntryCard";
import LoginCard from "@/components/auth/LoginCard";
import EmailSignUpCard from "@/components/auth/EmailSignUpCard";
import {
	cn,
	errorIntoString,
	formatRelativeTime,
	getDateDividerLabel,
	getInitials,
	isSameDay,
} from "@/lib/utils";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";
import type { CatalogSuggestion, Chat, ChatMessage, Message as MessageData, Participant } from "@/types";
import { OnboardingStages, USER_ONLINE_STATUS_MAPPING, UserOnlineStatus, UserPlan } from "@/types";

import {
	clearGuestLauraPendingResume,
	getGuestLauraPendingResume,
	GUEST_LAURA_DRAFT_HANDOFF_SUCCESS_EVENT,
	setStoredGuestLauraDraft,
	withCurrentPageUrl,
	type GuestLauraPageContext,
} from "@/lib/guest-laura-draft";
import { invalidateApiSessionCache } from "@/app/api";

export type GuestChatPageContext = GuestLauraPageContext;

type SupportChatPopupProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	pageContext?: GuestChatPageContext;
	catalogRequest?: { id: number; suggestions: CatalogSuggestion[] } | null;
};

type PopupInboxItem = {
	key: string;
	chat: Chat;
	supportView?: "customer" | "staff";
};

const TECHXEED_SUPPORT_ICON = "/assets/solo%20icon.png";

function senderIdOf(message: ChatMessage): string {
	const sender = message.senderId;
	return typeof sender === "string" ? sender : (sender?._id ?? "");
}

function isReceivedMessage(
	message: ChatMessage,
	userId?: string,
	supportView?: "customer" | "staff",
): boolean {
	if (supportView === "staff") {
		return message.senderType === "AI" || message.clientSupportSenderRole === "CUSTOMER";
	}
	if (supportView === "customer") {
		return (
			message.senderType === "AI" ||
			message.clientSupportSenderRole === "STAFF" ||
			(Boolean(userId) && senderIdOf(message) !== userId)
		);
	}
	return message.senderType === "AI" || (Boolean(userId) && senderIdOf(message) !== userId);
}

function getMessageSender(message: ChatMessage): Participant | null {
	return typeof message.senderId === "string" ? null : message.senderId;
}

function getSupportGroupUserId(chat: Chat) {
	return typeof chat.supportGroupUserId === "string"
		? chat.supportGroupUserId
		: (chat.supportGroupUserId as any)?._id;
}

function getPopupChatActivityTime(chat: Chat) {
	return new Date(
		chat.lastMessage?.createdAt ?? (chat as any).updatedAt ?? (chat as any).createdAt ?? 0,
	).getTime();
}

function getPreferredCustomerSupportItem(current: PopupInboxItem | null, next: PopupInboxItem) {
	if (!current) return next;
	if (next.chat.lastMessage && !current.chat.lastMessage) return next;
	if (!next.chat.lastMessage && current.chat.lastMessage) return current;
	return getPopupChatActivityTime(next.chat) > getPopupChatActivityTime(current.chat)
		? next
		: current;
}

function getSupportGroupCustomer(chat: Chat) {
	const supportGroupUserId = getSupportGroupUserId(chat);
	return chat.participants.find((participant) => participant._id === supportGroupUserId);
}

function cleanAvatarUrl(avatar?: string | null) {
	return avatar?.trim() || "";
}

function getPopupChatLabel(chat: Chat, userId?: string, supportView?: "customer" | "staff") {
	if (chat.chatType === "CLIENT_SUPPORT_GROUP") {
		const customer = getSupportGroupCustomer(chat);
		if (supportView === "staff" && customer) {
			return (
				[customer.firstName, customer.lastName].filter(Boolean).join(" ") ||
				customer.email ||
				"Client Support"
			);
		}
		return "Client Support";
	}
	if (chat.chatType === "CUSTOM_GROUP") return chat.groupName || "Group chat";

	const receiver = chat.participants.find((participant) => participant._id !== userId);
	return [receiver?.firstName, receiver?.lastName].filter(Boolean).join(" ") || "Conversation";
}

function getPopupChatAvatar(chat: Chat, userId?: string, supportView?: "customer" | "staff") {
	if (chat.chatType === "CLIENT_SUPPORT_GROUP") {
		return supportView === "staff"
			? cleanAvatarUrl(getSupportGroupCustomer(chat)?.avatar)
			: "/Main Icon.jpg";
	}
	if (chat.chatType === "CUSTOM_GROUP")
		return cleanAvatarUrl(chat.groupAvatar) || "/Main Icon.jpg";
	return cleanAvatarUrl(
		chat.participants.find((participant) => participant._id !== userId)?.avatar,
	);
}

function getPopupChatReceiver(
	chat: Chat,
	fallbackReceiver: Participant,
	userId?: string,
	supportView?: "customer" | "staff",
): Participant {
	if (chat.chatType === "CLIENT_SUPPORT_GROUP") {
		return supportView === "staff"
			? (getSupportGroupCustomer(chat) ?? fallbackReceiver)
			: fallbackReceiver;
	}
	if (chat.chatType === "CUSTOM_GROUP") {
		return {
			_id: chat._id,
			firstName: chat.groupName || "Group chat",
			lastName: "",
			email: "",
			avatar: chat.groupAvatar || "/Main Icon.jpg",
			bio: "",
			onlineStatus: UserOnlineStatus.ONLINE,
			plan: { duration: UserPlan.free, planID: "" },
			role: "CUSTOMER",
			companyName: "",
		} as Participant;
	}
	return chat.participants.find((participant) => participant._id !== userId) ?? fallbackReceiver;
}

function getPopupChatPreview(chat: Chat) {
	const content = chat.lastMessage?.content?.trim();
	return content || "No messages yet";
}

function getSupportReceiver(): Participant {
	return {
		_id: "client-support-group",
		firstName: "Client",
		lastName: "Support",
		email: "support@techxeed.com",
		avatar: "/Main Icon.jpg",
		bio: "",
		onlineStatus: UserOnlineStatus.ONLINE,
		plan: { duration: UserPlan.free, planID: "" },
		role: "ADMIN",
		companyName: "Techxeed",
	};
}

export default function SupportChatPopup({
	open,
	onOpenChange,
	pageContext,
	catalogRequest,
}: SupportChatPopupProps) {
	const { data: session, status: sessionStatus } = useSession();
	const { toast } = useToast();
	const [guestDraft, setGuestDraft] = useState("");
	const [guestSentMessage, setGuestSentMessage] = useState<string | null>(null);
	const [guestCatalogSuggestions, setGuestCatalogSuggestions] = useState<CatalogSuggestion[]>([]);
	const [guestSentCatalogSuggestions, setGuestSentCatalogSuggestions] = useState<CatalogSuggestion[]>([]);
	const [guestTyping, setGuestTyping] = useState(false);
	const [guestComposerMenuOpen, setGuestComposerMenuOpen] = useState(false);
	const [showGuestAuthPrompt, setShowGuestAuthPrompt] = useState(false);
	const [guestAuthView, setGuestAuthView] = useState<"signup" | "login" | "emailSignup">(
		"signup",
	);
	const [activeChatId, setActiveChatId] = useState<string | null>(null);
	const [activeSupportView, setActiveSupportView] = useState<"customer" | "staff" | undefined>(
		undefined,
	);
	const [popupView, setPopupView] = useState<"chat" | "inbox">("chat");
	const [replyTarget, setReplyTarget] = useState<{ messageId: string; label: string } | null>(
		null,
	);
	const [typingUserName, setTypingUserName] = useState<string | null>(null);
	const [isLauraAsyncReplyPending, setIsLauraAsyncReplyPending] = useState(false);
	const scrollRef = useRef<HTMLUListElement>(null);
	const guestScrollRef = useRef<HTMLDivElement>(null);
	const guestUserMessageRef = useRef<HTMLDivElement>(null);
	const typingClearTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const guestTypingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lastPopupScrollTargetRef = useRef<string | null>(null);
	const lastPopupBottomScrollChatRef = useRef<string | null>(null);
	const forcePopupBottomScrollRef = useRef(false);
	const lauraTypingDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const lauraTypingStartedAtRef = useRef<number | null>(null);
	const userId = session?.user.id;
	const userOnboardingStage =
		session?.user.onboardingStages ??
		(session?.user as { onBoardingStages?: OnboardingStages } | undefined)?.onBoardingStages;
	const isEmailSignupVerificationPending =
		userOnboardingStage === OnboardingStages.SIGNUP_COMPLETED;
	const canUseAuthenticatedSupport = Boolean(
		session &&
			sessionStatus === "authenticated" &&
			session.idToken &&
			!isEmailSignupVerificationPending,
	);
	const isSupportStaff = session?.user.role === "ADMIN" || session?.user.role === "DIRECTOR";
	const supportReceiver = useMemo(() => getSupportReceiver(), []);
	useMessageSubscription(userId, open && canUseAuthenticatedSupport);

	const { data: supportChat, isFetching: supportChatFetching } =
		useGetClientSupportGroupChatQuery(undefined, {
			skip: !open || !canUseAuthenticatedSupport,
		});
	const { data: inboxChats, isFetching: inboxFetching } = useGetAllChatsQuery(
		{
			id: userId ?? "",
			page: 1,
			limit: CHAT_CONSTANTS.CHAT_LIST_LIMIT,
			archived: false,
		},
		{
			skip:
				!open ||
				!userId ||
				sessionStatus !== "authenticated" ||
				!canUseAuthenticatedSupport,
			pollingInterval: open && popupView === "inbox" ? CHAT_CONSTANTS.POLLING_INTERVAL : 0,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true,
		},
	);
	const chatId = activeChatId ?? supportChat?._id ?? null;
	const {
		data: messagesData,
		isFetching: messagesFetching,
		isError: messagesIsError,
		error: messagesError,
		refetch: refetchMessages,
	} = useGetChatsMessagesByIdQuery(
		{
			chatId: chatId ?? "",
			page: 1,
			limit: CHAT_CONSTANTS.MESSAGE_LIMIT,
			sortBy: "createdAt:desc",
		},
		{
			skip: !open || !chatId || popupView === "inbox" || !canUseAuthenticatedSupport,
			pollingInterval:
				open && chatId && popupView === "chat" && canUseAuthenticatedSupport
					? CHAT_CONSTANTS.POLLING_INTERVAL
					: 0,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true,
		},
	);
	const [markChatAsRead] = useMarkChatAsReadMutation();
	const visibleTypingUserName =
		isSupportStaff && typingUserName === "Laura" ? null : typingUserName;
	const statusLabel = visibleTypingUserName ? `${visibleTypingUserName} is typing...` : "Online";
	const currentPageContext = useMemo(() => withCurrentPageUrl(pageContext), [pageContext]);
	const guestReturnUrl =
		typeof window !== "undefined"
			? `${window.location.pathname}${window.location.search}${window.location.hash}`
			: undefined;

	useEffect(() => {
		const suggestions = catalogRequest?.suggestions;
		if (!catalogRequest?.id || !Array.isArray(suggestions) || suggestions.length === 0) return;

		setPopupView("chat");
		if (supportChat?._id) {
			setActiveChatId(supportChat._id);
			setActiveSupportView("customer");
		}

		if (!canUseAuthenticatedSupport) {
			const nextSuggestions = suggestions.slice(0, 5);
			setGuestCatalogSuggestions(nextSuggestions);
			setGuestDraft((current) =>
				current.trim()
					? current
					: `I'd like to discuss ${formatServiceDisplayTitle(nextSuggestions[0]?.title) || "this service"}.`,
			);
		}
	}, [canUseAuthenticatedSupport, catalogRequest?.id, catalogRequest?.suggestions, supportChat?._id]);

	const inboxItems = useMemo(() => {
		if (!userId) return [];
		const items = new Map<string, PopupInboxItem>();
		let customerSupportItem: PopupInboxItem | null = null;
		const staffSupportItems = new Map<string, PopupInboxItem>();
		if (supportChat?._id) {
			const item = {
				key: isSupportStaff ? `${supportChat._id}:customer` : "client-support:customer",
				chat: supportChat,
				supportView: "customer" as const,
			};
			if (isSupportStaff) {
				items.set(item.key, item);
			} else {
				customerSupportItem = getPreferredCustomerSupportItem(customerSupportItem, item);
			}
		}
		(inboxChats?.docs ?? []).forEach((chat) => {
			if (chat.chatType === "CLIENT_SUPPORT_GROUP") {
				if (!isSupportStaff) {
					if (getSupportGroupUserId(chat) !== userId) return;
					customerSupportItem = getPreferredCustomerSupportItem(customerSupportItem, {
						key: "client-support:customer",
						chat,
						supportView: "customer",
					});
					return;
				}
				if (supportChat?._id === chat._id) {
					items.set(`${chat._id}:customer`, {
						key: `${chat._id}:customer`,
						chat,
						supportView: "customer",
					});
					if (!isSupportStaff) return;
				}
				const supportGroupUserId = getSupportGroupUserId(chat) || chat._id;
				const staffKey = `client-support:${supportGroupUserId}:staff`;
				const staffItem = {
					key: staffKey,
					chat,
					supportView: "staff" as const,
				};
				staffSupportItems.set(
					staffKey,
					getPreferredCustomerSupportItem(
						staffSupportItems.get(staffKey) ?? null,
						staffItem,
					),
				);
				return;
			}
			if (!chat.lastMessage && chat.chatType !== "CUSTOM_GROUP") return;
			items.set(chat._id, { key: chat._id, chat });
		});
		if (customerSupportItem) {
			items.set(customerSupportItem.key, customerSupportItem);
		}
		staffSupportItems.forEach((item) => items.set(item.key, item));

		return Array.from(items.values()).sort((a, b) => {
			if (
				a.chat.chatType === "CLIENT_SUPPORT_GROUP" &&
				b.chat.chatType !== "CLIENT_SUPPORT_GROUP"
			)
				return -1;
			if (
				b.chat.chatType === "CLIENT_SUPPORT_GROUP" &&
				a.chat.chatType !== "CLIENT_SUPPORT_GROUP"
			)
				return 1;
			if (a.supportView === "customer" && b.supportView === "staff") return -1;
			if (b.supportView === "customer" && a.supportView === "staff") return 1;
			const aTime = a.chat.lastMessage?.createdAt
				? new Date(a.chat.lastMessage.createdAt).getTime()
				: 0;
			const bTime = b.chat.lastMessage?.createdAt
				? new Date(b.chat.lastMessage.createdAt).getTime()
				: 0;
			return bTime - aTime;
		});
	}, [inboxChats?.docs, isSupportStaff, supportChat, userId]);

	const currentInboxItem = useMemo(
		() =>
			inboxItems.find(
				(item) => item.chat._id === chatId && item.supportView === activeSupportView,
			) ??
			inboxItems.find((item) => item.chat._id === chatId) ??
			(supportChat
				? {
						key: `${supportChat._id}:customer`,
						chat: supportChat,
						supportView: "customer" as const,
					}
				: null),
		[activeSupportView, inboxItems, chatId, supportChat],
	);
	const currentChat = currentInboxItem?.chat ?? null;
	const isCurrentClientSupportChat =
		currentChat?.chatType !== undefined
			? currentChat.chatType === "CLIENT_SUPPORT_GROUP"
			: true;
	const clientSupportView = isCurrentClientSupportChat
		? (currentInboxItem?.supportView ?? "customer")
		: undefined;
	const isLauraDirectorsGroup = currentChat?.groupKey === "LAURA_DIRECTORS";
	const isLauraReplyBlockingComposer =
		isCurrentClientSupportChat &&
		clientSupportView !== "staff" &&
		currentChat?.lauraPaused !== true &&
		isLauraAsyncReplyPending;
	const currentReceiver = currentChat
		? getPopupChatReceiver(currentChat, supportReceiver, userId, clientSupportView)
		: supportReceiver;
	const currentChatLabel = currentChat
		? getPopupChatLabel(currentChat, userId, clientSupportView)
		: "Client Support";
	const currentChatAvatar = currentChat
		? getPopupChatAvatar(currentChat, userId, clientSupportView)
		: "/Main Icon.jpg";
	const currentChatInitials = getInitials(currentReceiver.firstName, currentReceiver.lastName);
	const isUserFacingClientSupportChat =
		isCurrentClientSupportChat && clientSupportView !== "staff";
	const userFacingClientSupportStatus = getUserFacingClientSupportStatus(
		isUserFacingClientSupportChat ? currentChat?.lauraPaused : false,
	);
	const userFacingClientSupportStatusLabel = visibleTypingUserName
		? `${visibleTypingUserName} is typing...`
		: isUserFacingClientSupportChat && currentChat?.lauraPaused
			? USER_ONLINE_STATUS_MAPPING[UserOnlineStatus.AWAY]
			: "Online";

	const messages = useMemo(
		() =>
			(messagesData?.docs ?? [])
				.slice()
				.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
		[messagesData?.docs],
	);

	const transformedMessages: MessageData[] = useMemo(
		() =>
			messages.map((item) => {
				const sender = getMessageSender(item);
				return {
					content: item.content,
					isReceived: isReceivedMessage(item, userId, clientSupportView),
					senderType: item.senderType,
					senderName:
						item.senderType === "AI"
							? item.senderDisplayName || "Laura"
							: sender
								? sender.firstName ||
									`${sender.firstName ?? ""} ${sender.lastName ?? ""}`.trim()
								: undefined,
					senderRole: item.senderType === "AI" ? "AI" : sender?.role,
					clientSupportSenderRole: item.clientSupportSenderRole,
					fileUrls: item.fileUrls,
					catalogSuggestions: item.catalogSuggestions,
					supportTicketDraft: item.supportTicketDraft,
					aiKnowledgeDraft: item.aiKnowledgeDraft,
					replyToMessage:
						item.replyToMessageId && typeof item.replyToMessageId === "object"
							? item.replyToMessageId
							: null,
					createdAt: item.createdAt,
					isRead: item.isRead,
					_id: item._id || item.id,
				};
			}),
		[clientSupportView, messages, userId],
	);

	const groupedMessages = useMemo(() => {
		const result: Array<
			| { type: "divider"; label: string }
			| { type: "message"; data: MessageData; isGrouped: boolean; messageId?: string }
		> = [];
		let currentDate: string | null = null;
		let previousSender: boolean | null = null;

		transformedMessages.forEach((item, index) => {
			const isNewDay = currentDate === null || !isSameDay(currentDate, item.createdAt);
			const nextMessage = transformedMessages[index + 1];
			const nextIsNewDay = !nextMessage || !isSameDay(item.createdAt, nextMessage.createdAt);
			const nextIsDifferentSender =
				!nextMessage || nextMessage.isReceived !== item.isReceived;
			const isLastInGroup = nextIsNewDay || nextIsDifferentSender;
			const isGrouped = !isNewDay && previousSender === item.isReceived && !isLastInGroup;

			if (isNewDay) {
				result.push({ type: "divider", label: getDateDividerLabel(item.createdAt) });
				currentDate = item.createdAt;
			}

			result.push({
				type: "message",
				data: item,
				isGrouped,
				messageId: item._id,
			});
			previousSender = item.isReceived;
		});

		return result;
	}, [transformedMessages]);

	const firstUnreadReceivedMessageId = useMemo(
		() => transformedMessages.find((item) => item.isReceived && item.isRead === false)?._id,
		[transformedMessages],
	);

	useEffect(() => {
		if (supportChat?._id && !activeChatId) {
			setActiveChatId(supportChat._id);
			setActiveSupportView("customer");
		}
	}, [activeChatId, supportChat?._id]);

	useEffect(() => {
		lastPopupScrollTargetRef.current = null;
		lastPopupBottomScrollChatRef.current = null;
		setReplyTarget(null);
	}, [chatId, open]);

	useLayoutEffect(() => {
		if (!open || popupView !== "chat" || !chatId || messages.length === 0) return;

		const animationFrame = window.requestAnimationFrame(() => {
			const container = scrollRef.current;
			if (!container) return;

			if (forcePopupBottomScrollRef.current) {
				container.scrollTop = container.scrollHeight;
				lastPopupBottomScrollChatRef.current = chatId;
				forcePopupBottomScrollRef.current = false;
				window.setTimeout(() => {
					void markChatAsRead({
						chatId,
						userId,
						supportView: clientSupportView,
					});
				}, 600);
				return;
			}

			if (firstUnreadReceivedMessageId) {
				const scrollTarget = `unread:${chatId}:${firstUnreadReceivedMessageId}`;
				if (lastPopupScrollTargetRef.current === scrollTarget) return;

				const unreadNode = document.getElementById(
					`message-${firstUnreadReceivedMessageId}`,
				);
				if (unreadNode) {
					container.scrollTop = Math.max(0, unreadNode.offsetTop - 8);
					lastPopupScrollTargetRef.current = scrollTarget;
					lastPopupBottomScrollChatRef.current = chatId;
					window.setTimeout(() => {
						void markChatAsRead({ chatId, userId, supportView: clientSupportView });
					}, 600);
					return;
				}
			}

			if (lastPopupBottomScrollChatRef.current === chatId) return;
			container.scrollTop = container.scrollHeight;
			lastPopupBottomScrollChatRef.current = chatId;
			if (firstUnreadReceivedMessageId) {
				window.setTimeout(() => {
					void markChatAsRead({
						chatId,
						userId,
						supportView: clientSupportView,
					});
				}, 600);
			}
		});

		return () => window.cancelAnimationFrame(animationFrame);
	}, [
		chatId,
		clientSupportView,
		firstUnreadReceivedMessageId,
		markChatAsRead,
		messages.length,
		open,
		popupView,
		userId,
	]);

	useEffect(() => {
		if (!open || !typingUserName || !chatId) return;

		const animationFrame = window.requestAnimationFrame(() => {
			const container = scrollRef.current;
			if (container) {
				container.scrollTop = container.scrollHeight;
				lastPopupBottomScrollChatRef.current = chatId;
			}
		});

		return () => window.cancelAnimationFrame(animationFrame);
	}, [chatId, open, typingUserName]);

	const showTyping = useCallback((name: string, duration: number | null = 5000) => {
		setTypingUserName(name);
		if (typingClearTimeoutRef.current) {
			clearTimeout(typingClearTimeoutRef.current);
		}
		if (duration === null) {
			typingClearTimeoutRef.current = null;
			return;
		}
		typingClearTimeoutRef.current = setTimeout(() => {
			setTypingUserName(null);
			typingClearTimeoutRef.current = null;
		}, duration);
	}, []);

	const startLauraAsyncTyping = useCallback(() => {
		const startedAt = Date.now();
		lauraTypingStartedAtRef.current = startedAt;

		if (lauraTypingDelayTimeoutRef.current) {
			clearTimeout(lauraTypingDelayTimeoutRef.current);
		}

		lauraTypingDelayTimeoutRef.current = setTimeout(() => {
			if (lauraTypingStartedAtRef.current === startedAt) {
				showTyping("Laura", null);
			}
			lauraTypingDelayTimeoutRef.current = null;
		}, 5000);
	}, [showTyping]);

	const lockLauraAsyncReply = useCallback(() => {
		setIsLauraAsyncReplyPending(true);
		setTypingUserName((current) => (current === "Laura" ? null : current));
	}, []);

	const cancelLauraAsyncReply = useCallback(() => {
		setIsLauraAsyncReplyPending(false);
		lauraTypingStartedAtRef.current = null;
		if (lauraTypingDelayTimeoutRef.current) {
			clearTimeout(lauraTypingDelayTimeoutRef.current);
			lauraTypingDelayTimeoutRef.current = null;
		}
		setTypingUserName((current) => (current === "Laura" ? null : current));
	}, []);

	useEffect(() => {
		const lauraTypingStartedAt = lauraTypingStartedAtRef.current;
		if (!lauraTypingStartedAt) return;

		const hasNewLauraMessage = messages.some(
			(item) =>
				item.senderType === "AI" &&
				new Date(item.createdAt).getTime() >= lauraTypingStartedAt,
		);
		if (!hasNewLauraMessage) return;

		if (typingUserName === "Laura") {
			setTypingUserName(null);
		}
		setIsLauraAsyncReplyPending(false);
		lauraTypingStartedAtRef.current = null;
		if (lauraTypingDelayTimeoutRef.current) {
			clearTimeout(lauraTypingDelayTimeoutRef.current);
			lauraTypingDelayTimeoutRef.current = null;
		}
		if (typingClearTimeoutRef.current) {
			clearTimeout(typingClearTimeoutRef.current);
			typingClearTimeoutRef.current = null;
		}
	}, [messages, typingUserName]);

	useEffect(() => {
		const handleHandoffSuccess = (event: Event) => {
			if (!(event instanceof CustomEvent)) return;
			const chatIdFromEvent = (event.detail as { chatId?: string })?.chatId;
			if (chatIdFromEvent) {
				setActiveChatId(chatIdFromEvent);
				setPopupView("chat");
				setActiveSupportView("customer");
			}
			clearGuestLauraPendingResume();
			setGuestSentMessage(null);
			setGuestSentCatalogSuggestions([]);
			setShowGuestAuthPrompt(false);
			setGuestTyping(false);
			onOpenChange(true);
			lockLauraAsyncReply();
			startLauraAsyncTyping();
		};

		window.addEventListener(GUEST_LAURA_DRAFT_HANDOFF_SUCCESS_EVENT, handleHandoffSuccess);
		return () =>
			window.removeEventListener(GUEST_LAURA_DRAFT_HANDOFF_SUCCESS_EVENT, handleHandoffSuccess);
	}, [lockLauraAsyncReply, onOpenChange, startLauraAsyncTyping]);

	// Consume persisted resume after SSO/email return (event may have fired on /signup/sso).
	useEffect(() => {
		if (!open || !canUseAuthenticatedSupport) return;
		const pending = getGuestLauraPendingResume();
		if (!pending) return;
		if (pending.chatId) {
			setActiveChatId(pending.chatId);
			setActiveSupportView("customer");
		}
		setPopupView("chat");
		setGuestSentMessage(null);
		setGuestSentCatalogSuggestions([]);
		setShowGuestAuthPrompt(false);
		setGuestTyping(false);
		clearGuestLauraPendingResume();
		lockLauraAsyncReply();
		startLauraAsyncTyping();
	}, [canUseAuthenticatedSupport, lockLauraAsyncReply, open, startLauraAsyncTyping]);

	useEffect(() => {
		if (!open || !chatId) return;

		const handleTyping = (event: Event) => {
			if (!(event instanceof CustomEvent)) return;
			const detail = event.detail as {
				chatId?: string;
				senderId?: string;
				senderName?: string;
				isTyping?: boolean;
			};
			if (detail.chatId !== chatId || !detail.senderId || detail.senderId === userId) return;

			if (detail.isTyping) {
				const firstName = detail.senderName?.split(" ").filter(Boolean)[0] || "Support";
				if (
					isLauraAsyncReplyPending &&
					typingUserName !== "Laura" &&
					firstName.trim().toLowerCase() === "laura"
				) {
					return;
				}
				showTyping(firstName);
				return;
			}

			setTypingUserName(null);
			if (typingClearTimeoutRef.current) {
				clearTimeout(typingClearTimeoutRef.current);
				typingClearTimeoutRef.current = null;
			}
		};

		window.addEventListener(CHAT_TYPING_EVENT, handleTyping);
		return () => window.removeEventListener(CHAT_TYPING_EVENT, handleTyping);
	}, [chatId, isLauraAsyncReplyPending, open, showTyping, typingUserName, userId]);

	useEffect(() => {
		if (open) return;
		setTypingUserName(null);
		setIsLauraAsyncReplyPending(false);
		if (typingClearTimeoutRef.current) {
			clearTimeout(typingClearTimeoutRef.current);
			typingClearTimeoutRef.current = null;
		}
	}, [open]);

	useEffect(
		() => () => {
			if (typingClearTimeoutRef.current) {
				clearTimeout(typingClearTimeoutRef.current);
			}
			if (guestTypingTimeoutRef.current) {
				clearTimeout(guestTypingTimeoutRef.current);
			}
			if (lauraTypingDelayTimeoutRef.current) {
				clearTimeout(lauraTypingDelayTimeoutRef.current);
			}
		},
		[],
	);

	const submitGuestDraft = () => {
		const content = guestDraft.trim();
		const catalogSuggestions = guestCatalogSuggestions.slice(0, 5);
		if (!content && catalogSuggestions.length === 0) return;

		setGuestSentMessage(content || "Service catalog");
		setGuestSentCatalogSuggestions(catalogSuggestions);
		if (typeof window !== "undefined") {
			setStoredGuestLauraDraft({
				message: content || "Service catalog",
				catalogSuggestions,
				pageContext: currentPageContext,
				returnUrl: guestReturnUrl,
				createdAt: new Date().toISOString(),
			});
		}
		setGuestDraft("");
		setGuestCatalogSuggestions([]);
		setShowGuestAuthPrompt(false);
		setGuestAuthView("signup");
		setGuestTyping(true);

		if (guestTypingTimeoutRef.current) {
			clearTimeout(guestTypingTimeoutRef.current);
		}

		guestTypingTimeoutRef.current = setTimeout(() => {
			setShowGuestAuthPrompt(true);
			guestTypingTimeoutRef.current = null;
		}, 1400);
	};

	useEffect(() => {
		if (!guestSentMessage || (!guestTyping && !showGuestAuthPrompt)) return;

		const timer = window.setTimeout(() => {
			const container = guestScrollRef.current;
			const messageNode = guestUserMessageRef.current;
			if (!container || !messageNode) return;

			container.scrollTo({
				top: Math.max(0, messageNode.offsetTop - 8),
				behavior: "smooth",
			});
		}, 50);

		return () => window.clearTimeout(timer);
	}, [guestSentMessage, guestTyping, showGuestAuthPrompt]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				hideOverlay
				hideClose
				disableDefaultMotion
				className="fixed bottom-4 left-auto right-4 top-auto z-[60] flex h-[min(36rem,calc(100vh-2rem))] w-[min(calc(100vw-2rem),24rem)] max-w-none origin-bottom-right translate-x-0 translate-y-0 grid-rows-none flex-col gap-0 overflow-hidden rounded-[1.5rem] border border-[color:var(--color-border-standard)] bg-[var(--glass-background)] p-0 shadow-[var(--glass-shadow)] backdrop-blur-xl duration-200 [-webkit-backdrop-filter:var(--glass-backdrop-filter)] [backdrop-filter:var(--glass-backdrop-filter)] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom-2 data-[state=closed]:slide-out-to-right-2 data-[state=closed]:zoom-out-75 data-[state=open]:slide-in-from-bottom-2 data-[state=open]:slide-in-from-right-2 data-[state=open]:zoom-in-75 sm:rounded-[1.5rem] md:w-96 lg:right-[4.75rem]"
			>
				<DialogTitle className="sr-only">Client Support chat</DialogTitle>
				<DialogDescription className="sr-only">
					Send a message to Techxeed Client Support without leaving this page.
				</DialogDescription>

				{sessionStatus === "authenticated" && canUseAuthenticatedSupport ? (
					<ChatDummyContextProvider session={session}>
						<div className="relative flex h-16 shrink-0 items-center justify-between border-b border-[color:var(--color-border-standard)] bg-background/55 px-4 backdrop-blur-xl dark:bg-core-midnight/45">
							<div
								className={cn(
									"flex items-center gap-3",
									popupView === "inbox" && "flex-1 justify-center",
								)}
							>
								{popupView === "chat" ? (
									<button
										type="button"
										onClick={() => setPopupView("inbox")}
										className="inline-flex size-7 items-center justify-center text-brand-primary transition-colors hover:text-brand-primary/80"
										aria-label="Back to support inbox"
									>
										<ChevronLeft className="size-5" />
									</button>
								) : null}
								{popupView === "inbox" ? null : (
									<Avatar className="size-10 rounded-full">
										<AvatarImage src={currentChatAvatar} alt="" aria-hidden />
										<AvatarFallback className="bg-secondary/15 text-xs font-semibold text-secondary">
											{currentChatInitials}
										</AvatarFallback>
									</Avatar>
								)}
								<div
									className={cn(
										"min-w-0",
										popupView === "inbox" && "flex-1 text-center",
									)}
								>
									<div
										className={cn(
											"flex items-center gap-1 text-sm font-semibold text-foreground",
											popupView === "inbox" &&
												"justify-center gap-3 text-secondary",
										)}
									>
										{popupView === "inbox" ? (
											<>
												<ChatIcon
													className="size-5 shrink-0 text-secondary"
													aria-hidden
												/>
												<span>Support</span>
											</>
										) : (
											currentChatLabel
										)}
										{isCurrentClientSupportChat && popupView === "chat" ? (
											<img
												src="/assets/profile/tick_premium.png"
												alt=""
												aria-hidden
												draggable={false}
												className="size-3.5 shrink-0 self-center object-contain"
											/>
										) : null}
									</div>
									{popupView === "inbox" ? null : (
										<div className="flex items-center gap-1 text-xs text-muted-foreground">
											<>
												<StatusMarker
													status={
														isUserFacingClientSupportChat
															? userFacingClientSupportStatus
															: (currentReceiver.onlineStatus ??
																UserOnlineStatus.ONLINE)
													}
													className="!size-1.5"
												/>
												<span>
													{isUserFacingClientSupportChat
														? userFacingClientSupportStatusLabel
														: statusLabel}
												</span>
											</>
										</div>
									)}
								</div>
							</div>
							{popupView === "inbox" ? (
								<div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2">
									<Link
										href="/profile/inbox"
										className="inline-flex size-6 items-center justify-center text-muted-foreground transition-colors hover:text-secondary"
										aria-label="Open support page"
									>
										<ExternalLink className="size-4" />
									</Link>
									<button
										type="button"
										onClick={() => onOpenChange(false)}
										className="inline-flex size-6 items-center justify-center text-brand-primary transition-colors hover:text-brand-primary/80"
										aria-label="Minimize chat"
									>
										<Minus className="size-4" />
									</button>
								</div>
							) : (
								<button
									type="button"
									onClick={() => onOpenChange(false)}
									className="inline-flex size-6 items-center justify-center text-brand-primary transition-colors hover:text-brand-primary/80"
									aria-label="Minimize chat"
								>
									<Minus className="size-4" />
								</button>
							)}
						</div>

						{popupView === "inbox" ? (
							<div className="min-h-0 flex-1 overflow-y-auto bg-background-secondary/20">
								{inboxFetching && inboxItems.length === 0 ? (
									<div className="flex h-full items-center justify-center">
										<LoadingSpinner />
									</div>
								) : (
									<>
										{inboxItems.length > 0 ? (
											<div className="pb-2">
												{inboxItems.map((item, index) => {
													const chat = item.chat;
													const unreadCount =
														item.supportView === "customer"
															? (chat.clientSupportCustomerUnreadCount ??
																chat.unreadCount ??
																0)
															: item.supportView === "staff"
																? (chat.clientSupportStaffUnreadCount ??
																	chat.unreadCount ??
																	0)
																: (chat.unreadCount ?? 0);
													const avatar = getPopupChatAvatar(
														chat,
														userId,
														item.supportView,
													);
													const label = getPopupChatLabel(
														chat,
														userId,
														item.supportView,
													);
													const receiver = getPopupChatReceiver(
														chat,
														supportReceiver,
														userId,
														item.supportView,
													);
													const initials = getInitials(
														receiver.firstName,
														receiver.lastName,
													);
													const hasUnread = unreadCount > 0;
													const isSupportChat =
														item.supportView === "customer";
													return (
														<div key={item.key} className="relative">
															<button
																type="button"
																onClick={() => {
																	forcePopupBottomScrollRef.current =
																		true;
																	lastPopupScrollTargetRef.current =
																		null;
																	lastPopupBottomScrollChatRef.current =
																		null;
																	setActiveChatId(chat._id);
																	setActiveSupportView(
																		item.supportView,
																	);
																	setPopupView("chat");
																}}
																className={cn(
																	"relative my-0 grid h-20 w-full grid-cols-[3.5rem_1fr] items-center gap-2 rounded-none p-2 text-left transition-all after:absolute after:bottom-0 after:left-[4.5rem] after:right-0 after:h-px after:bg-[color:var(--color-border-standard)] hover:bg-accent-foreground/5",
																	hasUnread &&
																		"bg-accent-foreground/10",
																)}
															>
																<div className="relative size-14">
																	{isSupportChat ? (
																		<div
																			className={cn(
																				"flex size-14 items-center justify-center rounded-full",
																				hasUnread
																					? "bg-accent-foreground"
																					: "bg-[color:var(--color-brand-primary)]",
																			)}
																		>
																			<img
																				src={
																					TECHXEED_SUPPORT_ICON
																				}
																				alt=""
																				aria-hidden
																				draggable={false}
																				className="size-10 object-contain brightness-0 invert"
																			/>
																		</div>
																	) : (
																		<Avatar className="size-14 rounded-full">
																			<AvatarImage
																				src={avatar}
																				alt=""
																				aria-hidden
																			/>
																			<AvatarFallback className="bg-secondary/15 text-sm font-semibold text-secondary">
																				{initials}
																			</AvatarFallback>
																		</Avatar>
																	)}
																	<StatusMarker
																		status={
																			isSupportChat
																				? getUserFacingClientSupportStatus(
																						chat.lauraPaused,
																					)
																				: (receiver.onlineStatus ??
																					UserOnlineStatus.OFFLINE)
																		}
																		className="absolute bottom-0 right-0 ring-2 ring-background"
																	/>
																</div>

																<div className="flex min-w-0 flex-1 flex-col">
																	<div className="inline-flex min-w-0 items-center gap-1">
																		<h3
																			className={cn(
																				"truncate text-sm2 font-semibold",
																				hasUnread
																					? "text-accent-foreground"
																					: "text-muted-foreground",
																			)}
																		>
																			{label}
																		</h3>
																		{isSupportChat ? (
																			<img
																				src="/assets/profile/tick_premium.png"
																				alt=""
																				aria-hidden
																				draggable={false}
																				className="mb-[0.13rem] ml-1 size-4 shrink-0 self-center object-contain"
																			/>
																		) : null}
																		{chat.lastMessage
																			?.createdAt ? (
																			<span
																				className={cn(
																					"ml-auto flex-shrink-0 truncate text-end text-xs",
																					hasUnread
																						? "font-medium text-accent-foreground"
																						: "text-muted-foreground",
																				)}
																			>
																				{formatRelativeTime(
																					chat.lastMessage
																						.createdAt,
																				)}
																			</span>
																		) : null}
																	</div>
																	<div className="flex min-w-0 items-center justify-between gap-2">
																		<p
																			className={cn(
																				"max-w-[25ch] truncate pl-2 text-xs",
																				hasUnread
																					? "font-medium text-accent-foreground"
																					: "font-light text-grey-foreground",
																			)}
																		>
																			{getPopupChatPreview(
																				chat,
																			)}
																		</p>
																		{hasUnread ? (
																			<div className="mr-2 size-2.5 shrink-0 rounded-full bg-accent-foreground shadow-sm ring-2 ring-accent-foreground/20" />
																		) : null}
																	</div>
																</div>
															</button>
														</div>
													);
												})}
											</div>
										) : (
											<div className="flex min-h-32 flex-col items-center justify-center px-6 text-center text-sm text-muted-foreground">
												<MessageSquare className="mb-3 size-10 text-secondary/50" />
												No support conversations yet.
											</div>
										)}
										<SupportTicketsPanel
											showCreateButton
											userId={userId}
											edgeToEdgeRows
											className="!bg-transparent pb-3 pt-4"
										/>
									</>
								)}
							</div>
						) : (supportChatFetching && !supportChat) ||
						  (chatId && messagesFetching && !messagesData && !messagesIsError) ? (
							<div className="flex min-h-0 flex-1 items-center justify-center bg-background-secondary/20">
								<LoadingSpinner />
							</div>
						) : messagesIsError && chatId ? (
							<div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-background-secondary/20 px-6 text-center">
								<p className="text-sm font-medium text-foreground">
									Could not load messages
								</p>
								<p className="text-xs text-muted-foreground">
									{errorIntoString(messagesError)}
								</p>
								<button
									type="button"
									className="rounded-full bg-secondary px-4 py-2 text-sm font-medium text-secondary-foreground"
									onClick={() => {
										invalidateApiSessionCache();
										void refetchMessages();
									}}
								>
									Retry
								</button>
							</div>
						) : chatId ? (
							<>
								<ul
									ref={scrollRef}
									className="min-h-0 flex-1 overflow-y-auto bg-background-secondary/20 px-4 py-2"
								>
									{groupedMessages.map((item, index) => {
										if (item.type === "divider") {
											return (
												<DateDivider
													key={`${item.label}-${index}`}
													label={item.label}
												/>
											);
										}

										return (
											<Message
												key={`${item.data.createdAt}-${index}`}
												data={item.data}
												chatId={chatId}
												isGrouped={item.isGrouped}
												messageId={item.messageId}
												isLauraDirectorsGroup={isLauraDirectorsGroup}
												onAnswerLauraEscalation={(messageId, label) =>
													setReplyTarget({ messageId, label })
												}
												onReplyMessage={(messageId, label) =>
													setReplyTarget({ messageId, label })
												}
											/>
										);
									})}
									{visibleTypingUserName ? (
										<TypingIndicator key="typing-indicator" />
									) : null}
								</ul>
								<TextBar
									chatId={chatId}
									receiver={currentReceiver}
									isClientSupportGroup={isCurrentClientSupportChat}
									isLauraDirectorsGroup={isLauraDirectorsGroup}
									lauraPaused={currentChat?.lauraPaused === true}
									clientSupportSenderRole={
										isCurrentClientSupportChat
											? isSupportStaff
												? "STAFF"
												: "CUSTOMER"
											: undefined
									}
									replyToMessageId={replyTarget?.messageId ?? null}
									replyTargetLabel={replyTarget?.label ?? null}
									onClearReplyTarget={() => setReplyTarget(null)}
									compact
									glassmorphic
									disabledByLaura={isLauraReplyBlockingComposer}
									showLauraReplyHint={visibleTypingUserName === "Laura"}
									allowAttachments
									pageContext={
										isCurrentClientSupportChat && !isSupportStaff
											? currentPageContext
											: undefined
									}
									catalogRequest={isCurrentClientSupportChat ? catalogRequest : null}
									onExpectedAsyncReplyLock={lockLauraAsyncReply}
									onExpectedAsyncReplyStart={startLauraAsyncTyping}
									onExpectedAsyncReplyCancel={cancelLauraAsyncReply}
									onMessageSent={() => {
										requestAnimationFrame(() => {
											const node = scrollRef.current;
											if (node) node.scrollTop = node.scrollHeight;
										});
									}}
								/>
							</>
						) : (
							<div className="flex min-h-0 flex-1 items-center justify-center bg-background-secondary/20">
								<LoadingSpinner />
							</div>
						)}
					</ChatDummyContextProvider>
				) : sessionStatus === "loading" && !showGuestAuthPrompt ? (
					<div className="flex min-h-0 flex-1 items-center justify-center">
						<LoadingSpinner />
					</div>
				) : (
					<>
						<div className="flex h-16 shrink-0 items-center justify-between border-b border-[color:var(--color-border-standard)] bg-background/55 px-4 backdrop-blur-xl dark:bg-core-midnight/45">
							<div className="flex items-center gap-3">
								<img
									src="/Main Icon.jpg"
									alt=""
									aria-hidden
									draggable={false}
									className="size-10 rounded-full object-cover"
								/>
								<div>
									<div className="flex items-center gap-1 text-sm font-semibold text-foreground">
										Client Support
										<img
											src="/assets/profile/tick_premium.png"
											alt=""
											aria-hidden
											draggable={false}
											className="size-3.5 shrink-0 self-center object-contain"
										/>
									</div>
									<div className="flex items-center gap-1 text-xs text-muted-foreground">
										<StatusMarker
											status={UserOnlineStatus.ONLINE}
											className="!size-1.5"
										/>
										<span>Online</span>
									</div>
								</div>
							</div>
							<button
								type="button"
								onClick={() => onOpenChange(false)}
								className="inline-flex size-6 items-center justify-center text-muted-foreground transition-colors hover:text-secondary"
								aria-label="Minimize chat"
							>
								<Minus className="size-4" />
							</button>
						</div>
						<div className="flex min-h-0 flex-1 flex-col bg-background-secondary/20">
							<div
								ref={guestScrollRef}
								className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
							>
								<div className="flex flex-col gap-4">
									<div className="mb-[-0.875rem] ml-3 inline-flex items-center gap-1 self-start text-[0.65rem] font-semibold text-muted-foreground">
										<span>Laura</span>
										<img
											src="/assets/profile/tick_premium.png"
											alt=""
											aria-hidden
											draggable={false}
											className="size-3 shrink-0 self-center object-contain"
										/>
									</div>
									<div className="w-fit max-w-[85%] rounded-[1.5rem] rounded-bl-none bg-tertiary/80 px-4 py-4 text-left text-sm leading-relaxed text-foreground shadow-sm">
										<p>
											Hi there 👋, It's Laura from Techxeed. Is there anything
											I can help you with?
										</p>
									</div>
									{guestSentMessage ? (
										<div
											ref={guestUserMessageRef}
											className="flex flex-col items-end"
										>
											<div className="max-w-[85%] rounded-[1.5rem] rounded-br-none bg-secondary/85 p-3 text-left text-sm leading-relaxed text-secondary-foreground shadow-sm">
												{guestSentMessage}
											</div>
											{guestSentCatalogSuggestions.length > 0 ? (
												<div className="mt-2 flex max-w-[85%] flex-wrap justify-end gap-2">
													{guestSentCatalogSuggestions.map((suggestion) => (
														<div
															key={suggestion.url}
															className="flex items-center gap-2 rounded-lg bg-background p-2 text-sm text-foreground shadow-sm"
														>
															<FolderKanban className="size-4 shrink-0 stroke-secondary" />
															<span className="max-w-48 truncate">{formatServiceDisplayTitle(suggestion.title)}</span>
														</div>
													))}
												</div>
											) : null}
										</div>
									) : null}
									{guestTyping ? (
										<ul className="contents">
											<TypingIndicator />
										</ul>
									) : null}
									{showGuestAuthPrompt ? (
										<div className="w-full">
											{guestAuthView === "signup" ? (
												<SignupEntryCard
													returnUrl={guestReturnUrl}
													showOneClickSeparator
													glassmorphic
													hideTitle
													description="Sign up to connect with customer service"
													onSwitchToLogin={() =>
														setGuestAuthView("login")
													}
													onSwitchToEmailSignup={() =>
														setGuestAuthView("emailSignup")
													}
												/>
											) : guestAuthView === "login" ? (
												<LoginCard
													variant="modal"
													returnUrlOverride={guestReturnUrl}
													onSwitchToSignup={() =>
														setGuestAuthView("signup")
													}
													onAuthenticated={() =>
														setShowGuestAuthPrompt(false)
													}
													hideTitle
												/>
											) : (
												<EmailSignUpCard
													variant="modal"
													returnUrlOverride={guestReturnUrl}
													hideTitle
													description="Create new account"
													glassmorphic
													hideAvatarUpload
													onBack={() => setGuestAuthView("signup")}
													onVerified={() => {
														invalidateApiSessionCache();
														setGuestTyping(false);
														setShowGuestAuthPrompt(false);
													}}
												/>
											)}
										</div>
									) : null}
								</div>
							</div>
							<div className="grid min-h-16 grid-cols-[auto_auto_1fr_auto] items-center gap-1 border-t border-[color:var(--color-border-standard)] bg-background/55 px-3 py-3 shadow-[0_-4px_18px_rgba(7,168,188,0.12)] backdrop-blur-xl dark:bg-core-midnight/45">
								<div className="relative">
									<button
										type="button"
										className="inline-flex size-8 items-center justify-center rounded-full transition-colors hover:bg-accent-foreground/10"
										onClick={() =>
											setGuestComposerMenuOpen((current) => !current)
										}
										aria-label="Open chat actions"
										aria-expanded={guestComposerMenuOpen}
									>
										<Plus
											className={cn(
												"size-4 stroke-accent-foreground transition-transform hover:stroke-accent-foreground/70",
												guestComposerMenuOpen && "rotate-45",
											)}
										/>
									</button>
									{guestComposerMenuOpen ? (
										<div className="absolute bottom-full left-0 z-20 mb-2 flex min-w-44 flex-col gap-1 rounded-2xl border border-secondary/20 bg-background/95 p-2 text-sm shadow-xl backdrop-blur dark:bg-card/95">
											<button
												type="button"
												className="flex items-center gap-2 rounded-xl px-3 py-2 text-left text-foreground transition hover:bg-secondary/10"
												onClick={() => {
													setGuestComposerMenuOpen(false);
													setGuestDraft((current) =>
														current.trim()
															? current
															: "I'd like to explore the service catalog.",
													);
												}}
											>
												<FolderKanban className="size-4 stroke-secondary" />
												<span>Service catalog</span>
											</button>
										</div>
									) : null}
								</div>
								{guestCatalogSuggestions.length > 0 ? (
									<div className="col-span-full flex flex-wrap gap-2 rounded-t-xl bg-accent-foreground/5 px-3 py-2 shadow-sm dark:bg-core-midnight/50">
										{guestCatalogSuggestions.map((suggestion) => (
											<div
												key={suggestion.url}
												className="flex items-center gap-2 rounded-lg bg-background p-2 text-sm shadow-sm"
											>
												<FolderKanban className="size-4 shrink-0 stroke-secondary" />
												<span className="max-w-48 truncate">{formatServiceDisplayTitle(suggestion.title)}</span>
											</div>
										))}
									</div>
								) : null}
								<EmojiPicker
									onEmojiSelect={(emoji) =>
										setGuestDraft((current) => `${current}${emoji}`)
									}
									className="size-8"
									iconClassName="size-4"
								/>
								<textarea
									value={guestDraft}
									onChange={(event) => setGuestDraft(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === "Enter" && !event.shiftKey) {
											event.preventDefault();
											submitGuestDraft();
										}
									}}
									placeholder="Type a message"
									className="max-h-32 min-h-10 resize-none overflow-y-auto rounded-md border border-[color:var(--color-border-standard)] bg-white/75 px-2 py-2 text-sm text-foreground placeholder:text-muted-foreground backdrop-blur-sm focus:outline-none dark:bg-[rgba(11,21,27,0.72)] dark:text-foreground dark:placeholder:text-muted-foreground md:text-sm"
									style={
										{
											fieldSizing: "content",
										} as React.CSSProperties
									}
								/>
								<button
									type="button"
									disabled={!guestDraft.trim() && guestCatalogSuggestions.length === 0}
									onClick={submitGuestDraft}
									className={cn(
										"flex size-10 items-center justify-center rounded-full bg-secondary text-white transition-colors",
										!guestDraft.trim() &&
											guestCatalogSuggestions.length === 0 &&
											"cursor-not-allowed bg-secondary/70",
									)}
									aria-label="Send message"
								>
									<SendHorizontal
										className="size-4 !text-white"
										style={{ color: "white", stroke: "white" }}
										strokeWidth={2.5}
									/>
								</button>
							</div>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
